/* global google */

import get from 'lodash/get';
import first from 'lodash/first';
import isEmpty from 'lodash/isEmpty';
import keyBy from 'lodash/keyBy';
import keys from 'lodash/keys';
import {
  FormLabel, Grid, Paper, Tooltip, IconButton, Button,
} from '@mui/material';
import Map from '@mui/icons-material/Map';
import ErrorIcon from '@mui/icons-material/Error';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import MUIDataTable from 'mui-datatables';
import React from 'react';
import { withRouter } from 'react-router-dom';
import moment from 'moment';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';

import routeActions from '../../../actions/routes';
import reportActions from '../../../actions/reports';
import { showSuccessSnackbar as showSuccessSnackbarAction } from '../../../actions/snackbar';
import WrappedSpinner from '../../common/wrapped-spinner';
import { MarketSelect } from '../../common/market-select';
import { BrandSelect } from '../../common/brand-select';
import VehicleTypeSelect from '../../common/vehicle-type-select';
import DateRangeDialog from '../../common/date-range-dialog';
import TimeRangeDialog from '../../common/time-range-dialog';
import withPersistentSettings from '../../common/table-with-persistent-settings';
import Modal from '../../common/modal';
import { BaseRouteMap } from '../map/route-map';
import { HeatMap } from '../map/heat-map';
import {
  rideMinutesForDisplay,
  dateFormatForRides,
  convertTimeStringToReadable,
  timeZones,
} from '../../../lib/utils';
import { customSortWithDates } from '../../../lib/table-utils';
import saveLocalSettingsWrapper from './save-report-settings';

const LOCAL_STORAGE_KEY = 'ride-report-page';
const styles = {
  root: {
    width: '95%',
    margin: '0 auto',
    overflowX: 'auto',
  },
};

function formatTableData(rides, tz) {
  return rides.map((ride) => [
    get(ride, 'vehicle.humanId', ''),
    get(ride, 'vehicle.type', ''),
    get(ride, 'market.name', ''),
    ride.extId,
    ride.start ? moment.utc(ride.start).tz(tz).format(dateFormatForRides(ride.start)) : 'No Start Time',
    ride.end ? moment.utc(ride.end).tz(tz).format(dateFormatForRides(ride.end)) : 'Not Completed',
    ride.end ? rideMinutesForDisplay(ride.durationSeconds) : '–',
    (ride.distanceMeters / 1000).toFixed(2),
    get(ride, 'startLocation.name'),
    get(ride, 'endLocation.name'),
    get(ride, 'routeId'),
  ]);
}

class BaseRidePage extends React.Component {
  constructor(props) {
    super(props);

    const {
      selectedBrand, marketId, startDate, endDate, timeRange, selectedVehicleType,
    } = props;

    const initialTz = first(timeZones);

    this.state = {
      selectedVehicleType,
      selectedBrand,
      selectedMarket: marketId,
      startDate: startDate ? moment.tz(startDate, initialTz).startOf('day') : moment.tz(initialTz).startOf('day'),
      endDate: endDate ? moment.tz(endDate, initialTz).endOf('day') : moment.tz(initialTz).endOf('day'),
      timeRange: timeRange || [],
      isHeatMapOpen: false,
      isRouteMapOpen: false,
      routeId: undefined,
    };

    this.handleChange = this.handleChange.bind(this);
    this.handleDateChange = this.handleDateChange.bind(this);
    this.handleTimeChange = this.handleTimeChange.bind(this);
    this.handleRouteClick = this.handleRouteClick.bind(this);
    this.handleHeatMapClick = this.handleHeatMapClick.bind(this);
    this.handleCsvClick = this.handleCsvClick.bind(this);
    this.getQueryArguments = this.getQueryArguments.bind(this);
    this.handleMarketChange = this.handleMarketChange.bind(this);
  }

  componentDidMount() {
    const { fetchRideReportData } = this.props;

    const query = this.getQueryArguments();
    fetchRideReportData(query);
  }

  componentDidUpdate(prevProps, prevState) {
    const {
      fetchRideReportData,
      heatMapData,
      rides,
      saveReportSettings,
    } = this.props;
    const {
      startDate,
      endDate,
      selectedBrand,
      selectedMarket,
      selectedVehicleType,
      timeRange,
    } = this.state;
    const isStartDateChanged = !moment(startDate).isSame(moment(prevState.startDate));
    const isEndDateChanged = !moment(endDate).isSame(moment(prevState.endDate));
    const isBrandChanged = selectedBrand !== prevState.selectedBrand;
    const isMarketChanged = selectedMarket !== prevState.selectedMarket;
    const isVehicleTypeChanged = selectedVehicleType !== prevState.selectedVehicleType;
    const startTime = timeRange && timeRange[0];
    const endTime = timeRange && timeRange[1];
    const isStartTimeChanged = startTime !== prevState.timeRange[0];
    const isEndTimeChanged = endTime !== prevState.timeRange[1];
    const isTimeChanged = isStartTimeChanged || isEndTimeChanged;

    // any state different, then re-fetch
    if (
      isStartDateChanged
      || isEndDateChanged
      || isBrandChanged
      || isMarketChanged
      || isVehicleTypeChanged
      || isTimeChanged
    ) {
      const newSettings = {
        startDate: startDate.format(),
        endDate: endDate.format(),
        selectedBrand,
        selectedVehicleType,
        marketId: selectedMarket,
        timeRange,
      };
      saveReportSettings(newSettings);

      const skipFetch = !selectedMarket || selectedMarket.length === 0;
      if (skipFetch) return; // don't fetch without marketId

      const query = this.getQueryArguments();
      fetchRideReportData(query);
    }

    if (heatMapData && isEmpty(prevProps.heatMapData)) {
      this.setState({ isHeatMapOpen: true });
    }

    const hasRides = !isEmpty(rides);
    const noPreviousRides = isEmpty(prevProps.rides);
    const hasNewRides = hasRides && rides.length !== prevProps.rides.length;
    if ((hasRides && noPreviousRides) || (hasNewRides)) {
      this.setState({ ridesById: keyBy(rides, (ride) => ride._id) });
    }
  }

  get tableOptions() {
    const basicOptions = get(this, 'props.tableOptions', {});
    const localOptions = {
      rowsPerPage: 1000,
      rowsPerPageOptions: [20, 50, 100, 1000],
    };

    const dateColumns = [4, 5]; // NOTE: manual reference to date columns
    basicOptions.customSort = (data, colIndex, order) => (
      customSortWithDates(data, colIndex, order, dateColumns)
    );

    return { ...basicOptions, ...localOptions };
  }

  get columns() {
    const {
      selectedFilters,
      primarySort,
      addToColumnData,
      markets,
    } = this.props;
    const { selectedMarket } = this.state;

    const foundMarket = markets && markets.find((m) => m._id === selectedMarket);
    const marketTz = get(foundMarket, 'tz', 'America/New_York');

    const columnData = [
      { name: 'Vehicle' },
      { name: 'Vehicle Type' },
      { name: 'Market' },
      { name: 'Ride ID' },
      {
        name: 'Start',
        options: {
          customBodyRender: (date) => {
            if (date) {
              return (
                <div>
                  <span>{date}</span>
                  <br />
                  <span>{marketTz}</span>
                </div>
              );
            } else {
              return '';
            }
          },
        },
      },
      {
        name: 'End',
        options: {
          customBodyRender: (date) => {
            if (date) {
              return (
                <div>
                  <span>{date}</span>
                  <br />
                  <span>{marketTz}</span>
                </div>
              );
            } else {
              return '';
            }
          },
        },
      },
      { name: 'Duration(min)' },
      { name: 'Distance (km)' },
      { name: 'Start Location' },
      { name: 'End Location' },
      {
        name: 'Map',
        options: {
          filter: false,
          sort: false,
          customBodyRender: (routeId) => {
            if (routeId) {
              return (
                <Tooltip title="Click to see trip route on map">
                  <IconButton onClick={() => this.handleRouteClick(routeId)}>
                    <Map />
                  </IconButton>
                </Tooltip>
              );
            } else {
              return '';
            }
          },
        },
      },
    ];
    return addToColumnData(columnData, selectedFilters, primarySort);
  }

  get mappableRoute() {
    const { routeLoading, routeBucket } = this.props;
    const { routeId } = this.state;
    if (routeBucket) {
      const matchingRoute = routeBucket[routeId];
      if (matchingRoute && !routeLoading) return matchingRoute.features;
    }
    return null;
  }

  get heatMapData() {
    const { heatMapData } = this.props;
    const coords = get(heatMapData, 'coords');
    if (coords && coords.length) {
      return coords.map((c) => new google.maps.LatLng(c[1], c[0]));
    }
    return null;
  }

  getQueryArguments() {
    const {
      startDate,
      endDate,
      selectedBrand,
      selectedMarket,
      selectedVehicleType,
      timeRange,
    } = this.state;

    const startTime = timeRange && timeRange[0];
    const endTime = timeRange && timeRange[1];

    const query = {};
    if (startDate || endDate) {
      query.start_date = moment(startDate).startOf('day').format('x');
      query.end_date = moment(endDate).endOf('day').format('x');
    }
    if (startTime && endTime) {
      query.start_time = startTime;
      query.end_time = endTime;
    }
    if (selectedBrand) query.brand = selectedBrand;
    if (selectedMarket) query.market_id = selectedMarket;
    if (selectedVehicleType) query.type = selectedVehicleType;

    return query;
  }

  handleChange(event) {
    this.setState({ [event.target.name]: event.target.value });
  }

  handleDateChange(startDate, endDate) {
    const { markets } = this.props;
    const { selectedMarket } = this.state;
    const foundMarket = markets && markets.find((m) => m._id === selectedMarket);
    const marketTz = get(foundMarket, 'tz', 'America/New_York');

    this.setState({
      startDate: moment.tz(startDate, marketTz).startOf('day'),
      endDate: moment.tz(endDate, marketTz).endOf('day'),
    });
  }

  handleMarketChange(marketId) {
    const { markets } = this.props;
    const { startDate, endDate } = this.state;

    const foundMarket = markets && markets.find((m) => m._id === marketId);
    const marketTz = get(foundMarket, 'tz', 'America/New_York');

    const updatedStartDate = moment.tz(startDate, marketTz).startOf('day');
    const updatedEndDate = moment.tz(endDate, marketTz).endOf('day');

    this.setState({
      selectedMarket: marketId,
      startDate: updatedStartDate,
      endDate: updatedEndDate,
    });
  }

  handleTimeChange(timeRange) {
    this.setState({ timeRange });
  }

  async handleRouteClick(routeId) {
    const { fetchRouteById } = this.props;
    await fetchRouteById(routeId);
    this.setState({
      isRouteMapOpen: true,
      routeId,
    });
  }

  handleHeatMapClick() {
    const { rides, heatMapIsLoading } = this.props;
    if (heatMapIsLoading) return;

    const { ridesById } = this.state;
    const { fetchRoutesForRides, preLoadRoutesForRides } = this.props;
    if (rides && rides.length) {
      preLoadRoutesForRides();
      fetchRoutesForRides(keys(ridesById));
    }
  }

  async handleCsvClick() {
    const { fetchAndDownloadRideReportCsv, showSuccessSnackbar } = this.props;
    const query = this.getQueryArguments();
    query.report_type = 'csv';

    showSuccessSnackbar('Downloading report; this may take up to a minute.');

    fetchAndDownloadRideReportCsv(query);
  }

  render() {
    const {
      marketsLoading,
      reportsLoading,
      heatMapIsLoading,
      rides,
      getTitleFromData,
      markets,
      isReportCsvLoading,
    } = this.props;
    const {
      startDate,
      endDate,
      selectedBrand,
      selectedMarket,
      selectedVehicleType,
      isHeatMapOpen,
      isRouteMapOpen,
      timeRange,
    } = this.state;

    const timeFrame = moment(endDate).diff(moment(startDate), 'days') + 1;
    const withinHeatmapTimeframe = timeFrame <= 10;
    const isLoading = marketsLoading || reportsLoading;
    const [startTime, endTime] = timeRange;
    const startStr = convertTimeStringToReadable(startTime);
    const endStr = convertTimeStringToReadable(endTime);
    const foundMarket = markets && markets.find((m) => m._id === selectedMarket);
    const marketTz = get(foundMarket, 'tz', 'America/New_York');

    // has a selected market & not already downloading a report
    const isCsvDownloadDisabled = !selectedMarket || !selectedMarket.length || isReportCsvLoading;

    return (
      <Paper style={styles.root} sx={{ p: 3 }}>
        <Grid container>
          <Grid item xs={2} sx={{ mr: 2 }}>
            <MarketSelect value={selectedMarket} handleChange={this.handleMarketChange} />
          </Grid>
          <Grid item xs={1} sx={{ mr: 1, mt: -1 }}>
            <BrandSelect
              value={selectedBrand}
              handleChange={(brand) => this.setState({ selectedBrand: brand })}
            />
          </Grid>
          <Grid item xs={1} sx={{ mr: 2, mt: -1 }}>
            <VehicleTypeSelect
              value={selectedVehicleType}
              handleChange={(type) => this.setState({ selectedVehicleType: type })}
            />
          </Grid>
          <Grid item xs={1} sx={{ mr: 1 }}>
            <FormLabel component="legend">Start Date</FormLabel>
            {startDate && startDate.format('MM-DD-YYYY')}
          </Grid>
          <Grid item xs={1} sx={{ mr: 1 }}>
            <FormLabel component="legend">End Date</FormLabel>
            {endDate && endDate.format('MM-DD-YYYY')}
          </Grid>
          <Grid item xs={1}>
            <FormLabel component="legend">Time Range</FormLabel>
            {(timeRange && timeRange.length === 2)
              ? `${startStr} - ${endStr}`
              : 'None'}
          </Grid>
          <Grid item xs={1}>
            <DateRangeDialog
              to={endDate}
              from={startDate}
              handleDateChange={this.handleDateChange}
            />
          </Grid>
          <Grid item xs={1} sx={{ mr: 1 }}>
            <TimeRangeDialog
              handleTimeChange={this.handleTimeChange}
            />
          </Grid>
          <Grid item xs={1}>
            {(withinHeatmapTimeframe && !isLoading && selectedMarket) ? (
              <Button
                onClick={this.handleHeatMapClick}
                color="secondary"
                variant="contained"
              >
                {heatMapIsLoading
                  ? 'Loading...'
                  : 'Heat Map'}
              </Button>
            ) : (
              <Tooltip title="Select one market and a < 10 day period to view heatmap">
                <div>
                  <ErrorIcon />
                  <Button
                    disabled
                    color="secondary"
                    variant="contained"
                  >
                    Heat Map
                  </Button>
                </div>
              </Tooltip>
            )}
          </Grid>
          <Grid item xs={1}>
            <Tooltip title="Download trips for Market">
              <div>
                <Button
                  disabled={isCsvDownloadDisabled}
                  color="secondary"
                  variant="contained"
                  onClick={this.handleCsvClick}
                >
                  <CloudDownloadIcon />
                  Trip CSV
                </Button>
              </div>
            </Tooltip>
          </Grid>
          <Grid item xs={12}>
            <WrappedSpinner isLoading={isLoading}>
              {rides && (
                <MUIDataTable
                  title={getTitleFromData('Trips Report', rides)}
                  columns={this.columns}
                  data={formatTableData(rides, marketTz)}
                  options={this.tableOptions}
                />
              )}
            </WrappedSpinner>
          </Grid>
        </Grid>
        <Modal
          fullWidth
          isOpen={isRouteMapOpen}
          onClose={() => this.setState(() => ({
            isRouteMapOpen: false,
            routeId: undefined,
          }))}
        >
          <BaseRouteMap route={this.mappableRoute} />
        </Modal>
        <Modal
          fullWidth
          isOpen={isHeatMapOpen}
          onClose={() => this.setState(() => ({
            isHeatMapOpen: false,
          }))}
        >
          <HeatMap heatMap={this.heatMapData} selectedMarket={selectedMarket} />
        </Modal>
      </Paper>
    );
  }
}

const mapStateToProps = ({ markets, reports, routes }) => ({
  reportsLoading: reports.isLoading,
  isReportCsvLoading: !!reports.isReportCsvLoading,
  rides: get(reports, 'rides', []),
  markets: markets.markets,
  marketsLoading: markets.isLoading,
  selectedMarket: markets.selectedMarket,
  routeLoading: routes.isLoading,
  heatMapIsLoading: routes.heatMapIsLoading,
  routeBucket: routes.byId,
  heatMapData: routes.heatMapData,
});

const mapDispatchToProps = (dispatch) => bindActionCreators({
  fetchRideReportData: reportActions.fetchRideReportData,
  fetchAndDownloadRideReportCsv: reportActions.fetchAndDownloadRideReportCsv,
  fetchRouteById: routeActions.fetchRouteById,
  fetchRoutesForRides: routeActions.fetchRoutesForRides,
  preLoadRoutesForRides: routeActions.preLoadRoutesForRides,
  showSuccessSnackbar: showSuccessSnackbarAction,
}, dispatch);

const RidePage = saveLocalSettingsWrapper(
  withPersistentSettings(
    connect(mapStateToProps, mapDispatchToProps)(withRouter(BaseRidePage)),
    LOCAL_STORAGE_KEY,
  ),
  LOCAL_STORAGE_KEY,
);

export { RidePage, BaseRidePage };
export default RidePage;
